import { get, merge, head, defaultTo, filter, set } from 'lodash';

// local dependencies
import { HttpService } from 'core/modules/network-service';
import { SERVICE_RESPONSE_STATUS, HTTP_RESPONSE_STATUS } from 'constants/common';
import { getStore } from 'store';
import { REDUCER, MEMBER_SEARCH_MODULE } from 'constants/entity-names';
import { isInbound } from 'resolvers/callStateEntities';
import { getVisibleItems } from 'resolvers/getVisibleEntities';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc AuthFlowCallerIdentificationPharmacyWidget defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { AuthFlowCallerIdentificationPharmacyWidgetService } from 'services/authFlowCallerIdentificationPharmacyWidget';
 *
 * // create service instance and send/cancel request
 * const authFlowCallerIdentificationPharmacyWidgetService = new AuthFlowCallerIdentificationPharmacyWidgetService();
 * const result = await authFlowCallerIdentificationPharmacyWidgetService.fetchData( '<user-id>' ); // fetch data
 * authFlowCallerIdentificationPharmacyWidgetService.cancel(); // cancel an active request
 */
export class AuthFlowCallerIdentificationPharmacyWidgetService extends HttpService {

    // make request and fetch data
    async fetchComponentData( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const module = isInbound() ? MEMBER_SEARCH_MODULE.CALLER_IDENTIFICATION: MEMBER_SEARCH_MODULE.CALLEE_IDENTIFICATION;

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callType,
                        module
                    },
                } ),
            } );

            const sections = get( response, 'body.component.structure.sections', {} );

            if( isInbound() ) {

                // remove the clinic number field
                const fields = get( sections, 'callerDetails.fields', [] );
                const newFields = filter( fields, field => {
                    return 'clinicNumber' !== field.name;
                } );
                set( sections, 'callerDetails.fields', newFields );
            }
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    sections: sections,
                    items: get( response, 'body.component.data.state.items', [] ),
                    options: get( response, 'body.component.data.options', {} ),
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // add or update title/Description item
	async addUpdateItem( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const module = isInbound() ? MEMBER_SEARCH_MODULE.CALLER_IDENTIFICATION: MEMBER_SEARCH_MODULE.CALLEE_IDENTIFICATION;

        // extract `operation` and actual payload
        const { operation, memberId, ...info } = data;

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: {
                        callId,
                        callType,
                        operation,
                        module
                    },
                    data: info,
                }
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    memberId: defaultTo( head( get( response, 'body.component.data.itemIds', [] ) ), memberId ),
                    ...info
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // add or update title/Description item
	async fetchMatchCriteria( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const module = isInbound() ? MEMBER_SEARCH_MODULE.CALLER_IDENTIFICATION: MEMBER_SEARCH_MODULE.CALLEE_IDENTIFICATION;

        // extract `operation` and actual payload
        const { id, idType } = data;

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: {
                        callId,
                        callType,
                        module
                    },
                    data: {
                        id: id,
                        idType: idType,
                    },
                }
            } );

            // extract data
			const visibility = get(
				response,
				'body.component.data.state.fieldVisibility',
				{}
			);
			const criteria = get( response, 'body.component.structure.criteria', [] );
			const values = get( response, 'body.component.data.state.values', [] );
			const mandatoryCriteria = get(
				response,
				'body.component.structure.mandatoryCriteria',
				[]
			);
			const optionalCriteria = get(
				response,
				'body.component.structure.optionalCriteria',
				[]
			);
			const actions = get( response, 'body.component.structure.actions', [] );
			const minOptionalCriteriaMatch = get(
				response,
				'body.component.data.state.config.minOptionalCriteriaMatch',
				null
			);
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    criteria: getVisibleItems( criteria, visibility ),
					values,
					mandatoryCriteria,
					optionalCriteria,
					actions,
					minOptionalCriteriaMatch,
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // update match criteria state
	async saveCriteriaSelection( { url = {}, data = {} } = {} ) {

		// get ongoing call info from the redux store
		const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
		const callerType = get(
			store.getState()[ REDUCER.CALL_STATE ],
			'callerType',
			null
		);

		try {
			const response = await this.post( {
				host: CONFIG.host,
				path: url,
				data: {
					context: {
						callId,
						callerType
					},
					data: data,
				},
			} );

			return {
				status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
				data: null,
			};
		} catch ( error ) {
			if ( HTTP_RESPONSE_STATUS.NONE === get( error, 'data.status', 0 ) ) {
				return {
					status: SERVICE_RESPONSE_STATUS.CANCELLED,
					data: null,
				};
			} else {
				return {
					status: SERVICE_RESPONSE_STATUS.FAILURE,
					data: null,
				};
			}
		}
	}

    // initiate an outbound call (for member)
    async initiateOutboundCall( { url = {}, data = {}, callerType = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const module = isInbound() ? MEMBER_SEARCH_MODULE.CALLER_IDENTIFICATION: MEMBER_SEARCH_MODULE.CALLEE_IDENTIFICATION;

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: {
                        callId,
                        callType,
                        module,
                        callerType
                    },
                    data: data,
                },
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    callData: get( response, 'body.component.data.state', {} )
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // register inbound calller (member)
    async registerInboundCaller( { url = {}, data = {}, callerType = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const module = isInbound() ? MEMBER_SEARCH_MODULE.CALLER_IDENTIFICATION: MEMBER_SEARCH_MODULE.CALLEE_IDENTIFICATION;
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: {
                        callId,
                        callType,
                        module,
                        callerType
                    },
                    data: data,
                },
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: null,
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // format error response
    _errorFormatter( errResponse ) {
        return errResponse; // some logic
    }
}
